import { useNavigate, useParams } from 'react-router-dom';
import { useLearner } from '../../hooks/useLearner';
import { useSeriesDetail, useStartPrewrittenStory } from '../../hooks/useLibrary';
import BottomNav from '../../components/common/BottomNav';
import CloudBand from '../../components/common/CloudBand';
import s from './SeriesDetailPage.module.css';

export default function SeriesDetailPage() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { data: activeLearner } = useLearner();
  const childId = activeLearner?.id;

  const { data, isLoading, error } = useSeriesDetail(seriesId, childId);
  const startMutation = useStartPrewrittenStory();

  const series = data?.series;
  const episodes = data?.episodes || [];

  async function handleStartEpisode(episode) {
    if (!episode.is_unlocked) return;

    if (episode.story_id) {
      navigate(`/story/read/${episode.story_id}`);
      return;
    }

    try {
      const result = await startMutation.mutateAsync({
        templateId: episode.id,
        childId,
      });
      navigate(`/story/read/${result.storyId}`);
    } catch (err) {
      if (err?.response?.data?.error?.locked) {
        const { required_level, current_level } = err.response.data.error;
        alert(`You need to reach Level ${required_level} to read this episode. You are Level ${Math.max(1, current_level)}.`);
      } else {
        alert('Could not start this story. Please try again.');
      }
    }
  }

  return (
    <div className={s.page}>
      <div
        className={s.hero}
        style={series?.cover_image_url ? { backgroundImage: `url(${series.cover_image_url})` } : {}}
      >
        <div className={s.heroOverlay} />
        <div className={s.heroContent}>
          <button className={s.backBtn} onClick={() => navigate('/library')} aria-label="Back to Library">
            ←
          </button>
          {isLoading ? (
            <div className={s.heroTitle}>Loading…</div>
          ) : (
            <>
              <h1 className={s.heroTitle}>{series?.name}</h1>
              {series?.description && <p className={s.heroDesc}>{series.description}</p>}
              <p className={s.heroMeta}>
                {episodes.length} {episodes.length === 1 ? 'episode' : 'episodes'}
                {series?.free_episode_count > 0 && (
                  <> · first {Math.min(series.free_episode_count, episodes.length)} free</>
                )}
              </p>
            </>
          )}
        </div>
        <CloudBand variant="cream" className={s.cloudBand} />
      </div>

      <div className={s.content}>
        {error && (
          <div className={s.errorState}>Couldn't load episodes. Please try again.</div>
        )}

        {!isLoading && !error && episodes.length === 0 && (
          <div className={s.emptyState}>No episodes available yet.</div>
        )}

        {episodes.map((episode) => {
          const isLocked = !episode.is_unlocked;
          const isStarted = !!episode.story_id;
          const isRead = episode.story_status === 'read' || episode.story_status === 'reviewed';
          const isStarting = startMutation.isPending && !episode.story_id;

          return (
            <button
              key={episode.id}
              className={`${s.episodeRow} ${isLocked ? s.locked : ''} ${isRead ? s.read : ''}`}
              onClick={() => handleStartEpisode(episode)}
              disabled={isLocked || isStarting}
              aria-label={isLocked ? `Episode ${episode.episode_number} — locked` : episode.title}
            >
              <div className={s.episodeNum}>
                {isLocked ? (
                  <span className={s.lockIcon} aria-hidden="true">🔒</span>
                ) : isRead ? (
                  <span className={s.readIcon} aria-hidden="true">✓</span>
                ) : (
                  <span className={s.numLabel}>E{episode.episode_number}</span>
                )}
              </div>

              <div className={s.episodeInfo}>
                <p className={s.episodeTitle}>{episode.title}</p>
                {isLocked ? (
                  <p className={s.episodeMeta}>
                    Unlock at Level {episode.required_level}
                  </p>
                ) : episode.is_free ? (
                  <p className={s.episodeMeta + ' ' + s.freeBadge}>Free</p>
                ) : (
                  <p className={s.episodeMeta}>
                    {isRead ? 'Completed' : isStarted ? 'In progress' : 'Unlocked'}
                  </p>
                )}
              </div>

              {!isLocked && (
                <span className={s.episodeArrow} aria-hidden="true">→</span>
              )}
            </button>
          );
        })}
      </div>

      <BottomNav />
    </div>
  );
}
